/**
 * The 2D view's camera in local metres (pure): the view rectangle, screen ↔ ENU conversion, zoom about a point,
 * framing a set of points, the GPU transform that carries the drawn view to a newer one between React renders,
 * and the marquee (box) selection rules.
 *
 * Pan and zoom move a CSS `matrix()` on the SVG (composited by the GPU, no React render, no SVG re-layout) and
 * commit the new view to React state only every few frames and when the gesture ends.
 */
import type { Vec2 } from "./geo.ts";

export interface View {
  cx: number;
  cy: number;
  /** Metres per CSS pixel. */
  mpp: number;
}

export const MIN_MPP = 0.004;
export const MAX_MPP = 1;

export const clampMpp = (mpp: number) => Math.min(MAX_MPP, Math.max(MIN_MPP, mpp));

/** A point of the canvas (CSS px from its top left) in ENU metres (north up). */
export function screenToEnu(v: View, w: number, h: number, sx: number, sy: number): Vec2 {
  return [(sx - w / 2) * v.mpp + v.cx, -(sy - h / 2) * v.mpp + v.cy];
}

export function enuToScreen(v: View, w: number, h: number, p: Vec2): Vec2 {
  return [(p[0] - v.cx) / v.mpp + w / 2, -(p[1] - v.cy) / v.mpp + h / 2];
}

/** Zoom by `factor` (> 1 zooms out) keeping the ENU point `at` under the pointer. */
export function zoomAt(v: View, at: Vec2, factor: number): View {
  const mpp = clampMpp(v.mpp * factor);
  const k = mpp / v.mpp;
  return { cx: at[0] + (v.cx - at[0]) * k, cy: at[1] + (v.cy - at[1]) * k, mpp };
}

/** The view showing `points` with `marginM` metres around them (null for no points). */
export function frameView(points: Vec2[], w: number, h: number, marginM = 3, minSpanM = 4): View | null {
  if (!points.length || w <= 0 || h <= 0) return null;
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
  const spanX = Math.max(x1 - x0, minSpanM) + 2 * marginM;
  const spanY = Math.max(y1 - y0, minSpanM) + 2 * marginM;
  return { cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, mpp: clampMpp(Math.max(spanX / w, spanY / h)) };
}

/** The CSS transform (origin at the top left) that makes a picture drawn with view `drawn` look like view
 * `target`: x' = k·x + tx, y' = k·y + ty. */
export function screenTransform(drawn: View, target: View, w: number, h: number): { k: number; tx: number; ty: number } {
  const k = drawn.mpp / target.mpp;
  return {
    k,
    tx: -k * (w / 2) + (drawn.cx - target.cx) / target.mpp + w / 2,
    ty: -k * (h / 2) + (target.cy - drawn.cy) / target.mpp + h / 2,
  };
}

export function sameView(a: View, b: View): boolean {
  return Math.abs(a.cx - b.cx) < 1e-9 && Math.abs(a.cy - b.cy) < 1e-9 && Math.abs(a.mpp - b.mpp) < 1e-12;
}

// ----------------------------------------------------------------------------------------- box selection
export interface Box {
  min: Vec2;
  max: Vec2;
}

export function boxOf(a: Vec2, b: Vec2): Box {
  return { min: [Math.min(a[0], b[0]), Math.min(a[1], b[1])], max: [Math.max(a[0], b[0]), Math.max(a[1], b[1])] };
}

/** SketchUp and AutoCAD's rule: dragging to the right (east on screen) selects what lies wholly inside the box
 * ("window"); dragging to the left selects whatever the box touches ("crossing"). */
export function marqueeMode(start: Vec2, end: Vec2): "window" | "crossing" {
  return end[0] >= start[0] ? "window" : "crossing";
}

const inside = (p: Vec2, b: Box) => p[0] >= b.min[0] && p[0] <= b.max[0] && p[1] >= b.min[1] && p[1] <= b.max[1];

function segmentsCross(a: Vec2, b: Vec2, c: Vec2, d: Vec2): boolean {
  const o = (p: Vec2, q: Vec2, r: Vec2) => Math.sign((q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]));
  return o(a, b, c) !== o(a, b, d) && o(c, d, a) !== o(c, d, b);
}

function pointInPolygon([x, y]: Vec2, ring: Vec2[]): boolean {
  let hitting = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hitting = !hitting;
  }
  return hitting;
}

/** Whether a polygon is selected by the box in `mode`. */
export function polygonInBox(poly: Vec2[], box: Box, mode: "window" | "crossing"): boolean {
  if (!poly.length) return false;
  if (mode === "window") return poly.every((p) => inside(p, box));
  if (poly.some((p) => inside(p, box))) return true;
  const corners: Vec2[] = [box.min, [box.max[0], box.min[1]], box.max, [box.min[0], box.max[1]]];
  if (corners.some((c) => pointInPolygon(c, poly))) return true;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]!, b = poly[(i + 1) % poly.length]!;
    for (let j = 0; j < 4; j++) if (segmentsCross(a, b, corners[j]!, corners[(j + 1) % 4]!)) return true;
  }
  return false;
}

/** The new selection from a box (or a click) with the modifiers: replace, Shift adds, Ctrl/⌘ toggles. */
export function combineSelection(current: string[], hits: string[], how: "replace" | "add" | "toggle"): string[] {
  if (how === "replace") return [...new Set(hits)];
  if (how === "add") return [...new Set([...current, ...hits])];
  const out = new Set(current);
  for (const h of new Set(hits)) {
    if (out.has(h)) out.delete(h);
    else out.add(h);
  }
  return [...out];
}

export function selectionMode(e: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean }, mac: boolean): "replace" | "add" | "toggle" {
  if (mac ? e.metaKey : e.ctrlKey) return "toggle";
  return e.shiftKey ? "add" : "replace";
}
